import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import {
	Table,
	Button,
	Box,
	SpaceBetween,
	TextFilter,
	Pagination,
	Header,
	CollectionPreferences,
	ButtonDropdown,
	Flashbar,
} from '@amzn/awsui-components-console';
import { useGenericTable } from './useGenericTable';
import { getDefaultStripedRows } from 'src/stores/uiPreferencesStore';
import EmptyState from '../EmptyState/EmptyState';
import { GenericTableProps } from './@types/GenericTable.types';
import { GenericComponentData } from 'src/@types/common';
import { generateTestId } from 'src/utils/testingUtils/testingHelper';



export type {
	GenericTableColumn,
	GenericTableAction,
	TableFormData,
} from './@types/GenericTable.types';
export type { GenericComponentData } from 'src/@types/common';

interface GenericTableRef {
	fetchData?: () => void;
}

function GenericTableComponent<T extends GenericComponentData>(
	{
		title,
		titleSuffix,
		headerVariant = 'h2',
		headerDescription,
		headerInfo,
		items,
		columns,
		isLoading = false,
		error = null,
		selectedItemId,
		onSelectionChange,
		onRefresh,
		primaryAction,
		dropdownActions = [],
		filterPlaceholder = 'Find items',
		filterFields = [],
		additionalHeaderActions,
		additionalFilters,
		flashItems,
		showErrorInEmptyState = true,
		emptyTitle = 'No items found',
		emptyDescription = 'No items found. Create a new item to get started.',
		emptyAction,
		collectionPreferences,
		defaultPreferences,
		resizableColumns = true,
		testIdPrefix,
		testIds = {},
	}: GenericTableProps<T>,
	ref: React.Ref<GenericTableRef>
) {
	const {
		state,
		dispatch,
		filteredItems,
		sortedItems,
		paginatedItems,
		filterCounter,
	} = useGenericTable(items, filterFields, defaultPreferences);

	useImperativeHandle(ref, () => ({
		fetchData: onRefresh,
	}));

	const handleSortingChange = (event: {
		detail: {
			sortingColumn: { sortingField?: string };
			isDescending?: boolean;
		};
	}) => {
		const { detail } = event;

		if (detail.sortingColumn.sortingField) {
			dispatch({
				type: 'SET_SORTING',
				payload: {
					field: detail.sortingColumn.sortingField,
					descending: detail.isDescending ?? false,
				},
			});
		}
	};

	const getSelectedItems = () => {
		if (!selectedItemId) return [];

		return paginatedItems.filter((item) => {
			const itemId =
				(item.id as string) ||
				(item.applicationId as string) ||
				(item.operationId as string);

			return itemId === selectedItemId;
		});
	};

	const getItemId = (item: T): string => {
		return (
			(item.id as string) ||
			(item.applicationId as string) ||
			(item.operationId as string) ||
			''
		);
	};

	const preferenceOptionIds = useMemo(() => {
		const options = collectionPreferences?.visibleContentPreference?.options;
		if (!options) return new Set<string>();
		return new Set(options.flatMap((group) => group.options.map((opt) => opt.id)));
	}, [collectionPreferences]);

	return (
		<SpaceBetween direction="vertical" size="l">
			{flashItems && flashItems.length > 0 && <Flashbar items={flashItems} />}
			<Table
				resizableColumns={resizableColumns}
				data-testid={testIds?.table || generateTestId(testIdPrefix || 'generic-table', 'table')}
				pagination={
					sortedItems.length > 0 && (
						<Pagination
							currentPageIndex={state.currentPageIndex}
							pagesCount={Math.ceil(
								sortedItems.length / state.preferences.pageSize!
							)}
							onChange={({ detail }) =>
								dispatch({
									type: 'SET_CURRENT_PAGE',
									payload: detail.currentPageIndex,
								})
							}
							ariaLabels={{
								nextPageLabel: 'Next page',
								previousPageLabel: 'Previous page',
								pageLabel: (pageNumber) =>
									`Page ${pageNumber} of ${Math.ceil(
										sortedItems.length / state.preferences.pageSize!
									)}`,
							}}
						/>
					)
				}
				header={
					<Header
						variant={headerVariant}
						description={headerDescription}
						info={headerInfo}
						actions={
							<SpaceBetween direction="horizontal" size="xs">
								{additionalHeaderActions}
								{primaryAction && (
									<Button
										variant="primary"
										onClick={primaryAction.onClick}
										disabled={primaryAction.disabled}
										disabledReason={primaryAction.disabledReason}
										loading={primaryAction.loading}
										data-testid={testIds?.primaryButton || generateTestId(testIdPrefix || 'generic-table', 'primary-button')}
									>
										{primaryAction.text}
									</Button>
								)}

								{dropdownActions.length > 0 && (
									<ButtonDropdown
										items={dropdownActions.map((action) => ({
											text: action.text,
											id: action.id,
											disabled: action.disabled,
											disabledReason: action.disabledReason,
										}))}
										onItemClick={(event) => {
											const { detail } = event;
											const action = dropdownActions.find(
												(a) => a.id === detail.id
											);

											if (action && selectedItemId) {
												action.handler(selectedItemId);
											}
										}}
										data-testid={testIds?.dropdown || generateTestId(testIdPrefix || 'generic-table', 'dropdown')}
										disabled={!selectedItemId}
										disabledReason="Please select an item to perform an action on."
									>
											Actions
									</ButtonDropdown>
								)}

								{onRefresh && (
									<Button
										onClick={onRefresh}
										iconName="refresh"
										variant="icon"
										data-testid={testIds?.refresh || generateTestId(testIdPrefix || 'generic-table', 'refresh')}
									/>
								)}
							</SpaceBetween>
						}
					>
						{title}{titleSuffix} ({paginatedItems.length})
					</Header>
				}
				variant="full-page"
				filter={
					(filterFields.length > 0 || additionalFilters) && (
						<SpaceBetween direction="horizontal" size="m" alignItems="end">
							{additionalFilters}
							{filterFields.length > 0 && (
								<TextFilter
									filteringText={state.filterText}
									onChange={({ detail }) =>
										dispatch({
											type: 'SET_FILTER_TEXT',
											payload: detail.filteringText,
										})
									}
									filteringPlaceholder={filterPlaceholder}
									countText={filterCounter(filteredItems.length)}
									data-testid={testIds?.filter || generateTestId(testIdPrefix || 'generic-table', 'filter')}
								/>
							)}
						</SpaceBetween>
					)
				}
				columnDefinitions={columns}
				columnDisplay={
					(state.preferences.visibleContent as string[]) && preferenceOptionIds.size > 0
						? columns.map((col) => ({
							id: col.id as string,
							visible: !preferenceOptionIds.has(col.id as string) ||
								(state.preferences.visibleContent as string[]).includes(col.id as string),
						}))
						: undefined
				}
				items={paginatedItems}
				loading={isLoading}
				loadingText={`Loading ${title.toLowerCase()}...`}
				selectionType="single"
				selectedItems={getSelectedItems()}
				onSelectionChange={({ detail }) => {
					if (detail.selectedItems.length > 0) {
						onSelectionChange(getItemId(detail.selectedItems[0]));
					} else {
						onSelectionChange(null);
					}
				}}
				sortingColumn={{ sortingField: state.sortingField }}
				sortingDescending={state.sortingDescending}
				wrapLines={state.preferences.wrapLines}
				stripedRows={state.preferences.stripedRows ?? getDefaultStripedRows()}
				onSortingChange={handleSortingChange}
				empty={
					<EmptyState
						title={emptyTitle}
						description={(showErrorInEmptyState ? error : null) ?? emptyDescription}
						testId={testIds?.emptyState || generateTestId(testIdPrefix || 'generic-table', 'empty-state')}
						action={emptyAction}
					/>
				}
				preferences={
					collectionPreferences && (
						<CollectionPreferences
							{...collectionPreferences}
							preferences={state.preferences}
							onConfirm={({ detail }) =>
								dispatch({
									type: 'SET_PREFERENCES',
									payload: detail,
								})
							}
						/>
					)
				}
				footer={
					<Box textAlign="right" color="text-body-secondary">
						{`Showing ${paginatedItems.length} of ${
							sortedItems.length
						} ${title.toLowerCase()}`}
					</Box>
				}
			/>
		</SpaceBetween>
	);
}

const GenericTable = forwardRef(GenericTableComponent) as <
	T extends GenericComponentData
>(
	props: GenericTableProps<T> & { ref?: React.Ref<GenericTableRef> }
) => React.ReactElement;

(GenericTable as React.ComponentType).displayName = 'GenericTable';

export default GenericTable;
